export type Language = "es" | "en";

export type TranslationValue = string | TranslationDictionary | Array<TranslationDictionary>;

export interface TranslationDictionary {
  [key: string]: TranslationValue;
}

export const translations: Record<Language, TranslationDictionary> = {
  es: {
    header: {
      title: "TFI Marketplace",
      subtitle:
        "Acompañamos a tu hogar para encontrar la mejor cobertura médica.",
      languageLabel: "Idioma",
      callus: "Conversa con un especialista",
      // Same source as the static HTML: REACT_APP_GOOGLE_ADS_PHONE_ES (.env)
      phone: process.env.REACT_APP_GOOGLE_ADS_PHONE_ES ?? "",
      languageToggleLabel: "Cambiar idioma a {{language}}",
      options: {
        es: "Español",
        en: "Inglés",
      },
      carrierCompareLabel: "Compara planes de los principales proveedores de seguros",
    },
    steps: {
      household: "Datos del hogar",
      plans: "Seleccione un plan para tu familia",
      details: "Complete los datos del responsable",
      confirmation: "Confirmación",
    },
    footer: {
      line1:
        "Las solicitudes, llamadas e invitaciones de seguros en este sitio se realizan a través de Max Capital 97 LLC, sólo donde está licenciada y designada.",
      line2:
        "El Número Nacional de Productor de Max Capital 97 LLC es 19966989 y sus licencias estatales se detallan a continuación.",
      line3:
        "The Future Insurance es una agencia de seguros privada y no está afiliada, respaldada ni operada por el gobierno de EE. UU., HealthCare.gov ni ningún Mercado de Seguros Médicos estatal o federal. No somos el sitio web del Mercado. Somos una agencia autorizada que ayuda a los consumidores a encontrar, comparar e inscribirse en planes de salud de la Ley de Cuidado de Salud Asequible (ACA) a través de socios aprobados por el Mercado. Al enviar su información, usted acepta ser contactado por un agente autorizado.",
      friendlyText:
        "Encontramos la mejor cobertura de salud para ti con transparencia y el cuidado que te mereces.",
      line4:
        "Copyright ©{{year}} | The Future Insurance. Todos los derechos reservados.",
    },
    benefits: {
      preTitle: "NUESTROS AGENTES OFRECEN PLANES PRIVADOS Y COMPATIBLES CON ACA",
      title1: "Beneficios Del",
      title2: "Seguro Médico",
      footerText: "Entre otros beneficios esenciales de salud cubiertos por la ley.",
      list: [
        { text: "Servicios de emergencia" },
        { text: "Servicios para pacientes ambulatorios" },
        { text: "Hospitalización" },
        { text: "Cuidado antes y después que nazca su bebé" },
        { text: "Servicios para la salud mental y abuso de sustancias" },
        { text: "Sus medicamentos recetados" },
        { text: "Fisioterapia, terapia ocupacional y rehabilitación" },
        { text: "Servicios de laboratorios" },
        { text: "Servicios preventivos: exámenes, vacunas y consejería" },
        { text: "Servicios pediátricos: cuidado dental y de la vista" }
      ]
    },
    faq: {
      preTitle: "Preguntas Frecuentes",
      title: "¿Tienes <blue>Dudas?</blue>",
      subtitle: "Respuestas a las preguntas más comunes sobre seguros de salud",
      questions: [
        {
          q: "¿Cuánto cuesta inscribirme?",
          a: "Inscribirte con nosotros es completamente gratis. No cobramos por la asesoría ni por ayudarte a revisar tus opciones. Solo pagarías la prima mensual del plan que elijas. Además, muchas personas califican para créditos fiscales que pueden reducir el costo mensual. La elegibilidad varía."
        },
        {
          q: "¿Puedo calificar para subsidios o ayuda financiera?",
          a: "En muchos casos, sí. La elegibilidad depende de factores como tus ingresos, el tamaño de tu familia y tu lugar de residencia. Durante la llamada, nuestros agentes revisan tu caso y te indican si calificas para recibir ayuda para bajar el costo de tu plan."
        },
        {
          q: "¿Qué documentos necesito para aplicar?",
          a: "Normalmente te pedirán tu número de Seguro Social o documento migratorio aplicable, información de ingresos como W-2, talones de pago o declaración de impuestos, y los datos de los familiares que deseas incluir en la cobertura."
        },
        {
          q: "¿Necesito ser ciudadano estadounidense para inscribirme?",
          a: "No necesariamente. Algunas personas con residencia permanente legal u otros estatus migratorios elegibles también pueden calificar para planes del Mercado de Salud. La elegibilidad depende de tu situación migratoria específica."
        },
        {
          q: "¿Cuánto tiempo toma el proceso de inscripción?",
          a: "En la mayoría de los casos, el proceso puede completarse en una sola llamada, siempre que tengas tu información y documentos básicos disponibles."
        },
        {
          q: "¿Puedo inscribirme si actualmente no tengo seguro médico?",
          a: "Sí. Si no tienes cobertura actualmente, podemos revisar qué opciones están disponibles para ti y si calificas para un período de inscripción especial o para la inscripción abierta, según tu caso."
        },
        {
          q: "¿Puedo incluir a mi esposa, hijos o familia en el mismo plan?",
          a: "Sí. Dependiendo del plan y de tu situación, puedes incluir a miembros de tu familia en la misma solicitud y revisar opciones de cobertura para todos."
        },
        {
          q: "¿Qué pasa si ya tengo un plan y quiero cambiarlo?",
          a: "Podemos revisar tu plan actual y ayudarte a comparar otras opciones disponibles. En algunos casos, podrás cambiar de plan si calificas para un período de inscripción especial o durante la inscripción abierta."
        },
        {
          q: "¿Cómo sé cuál plan me conviene más?",
          a: "Nuestros agentes te ayudan a comparar planes según tu presupuesto, medicamentos, doctores de preferencia y necesidades médicas. La idea es ayudarte a encontrar una opción que se ajuste a tu situación."
        }
      ]
    },
    buttons: {
      back: "Volver",
      cancel: "Cancelar",
      continue: "Continuar",
      viewDetails: "Ver detalles",
      close: "Cerrar",
      selectPlan: "Seleccionar plan",
      submitApplicant: "Enviar información",
      startOver: "Volver al inicio",
    },
    wizard: {
      stepCounter: "Fase {{current}} de {{total}} — {{phase}}",
      titles: {
        householdType: "Cotización",
        householdBasics: "Datos básicos del hogar",
        primary: "Datos del responsable",
        members: "Integrantes adicionales",
        identityOtp: "Verificación de identidad",
        plans: "Seleccione un plan",
        summary: "Resumen y confirmación",
        consent: "Firma electrónica",
        extra: "Información adicional",
        quoteHolder: "Datos del titular",
        quoteMembers: "Integrantes del plan",
        quotePayment: "Información de pago",
        quoteSuccess: "Cotización lista",
      },
      coverageType: {
        title: "¿Qué tipo de cobertura buscas?",
        helper: "Selecciona una opción para comenzar",
        options: {
          comprehensive: {
            title: "Cobertura más completa",
            desc: "Planes médicos mayores con protección total",
          },
          specific: {
            title: "Un plan con beneficios específicos",
            desc: "Cobertura enfocada en tus necesidades",
          },
          decide: {
            title: "Ayúdame a decidir el plan adecuado",
            desc: "Nuestros expertos te guían",
          },
          know: {
            title: "Ya sé qué plan quiero",
            desc: "Habla con un agente para inscribirte",
          },
          medicare: {
            title: "Opciones de Medicare",
            desc: "Para personas de 65 años o más",
          },
        },
      },
      common: {
        yearsOld: "años",
        dateOfBirth: {
          day: "Día",
          month: "Mes",
          year: "Año",
          months: {
            "01": "Enero", "02": "Febrero", "03": "Marzo", "04": "Abril",
            "05": "Mayo", "06": "Junio", "07": "Julio", "08": "Agosto",
            "09": "Septiembre", "10": "Octubre", "11": "Noviembre", "12": "Diciembre",
          },
        },
      },
      householdType: {
        title: "¿Cómo deseas tu cotización?",
        helper:
          "Seleccione la opción que mejor te represente para personalizar la experiencia",
        options: {
          soltero: "Soltero/a",
          pareja: "Pareja",
          familia: "Familia",
        },
      },
      householdBasics: {
        title: "Ingresa tu código postal e ingresos",
        helper:
          "Usamos esta información para encontrar planes con cobertura en {{household}}",
        zipCodeLabel: "Código postal",
        loadingZip: "Buscando código postal...",
        countyLabel: "Condado",
        incomeLabel: "Ingreso familiar anual",
        incomePlaceholder: "Ej: 45000",
        errors: {
          zip: "Ingresa un código postal válido",
          income: "Ingresa un ingreso familiar mayor a cero",
        },
      },
      primary: {
        title: "Cuéntanos sobre el responsable del hogar",
        helper:
          "Estos datos nos permiten mantenerte informado sobre tu solicitud",
        firstNameLabel: "Nombre",
        firstNamePlaceholder: "Ej: Ana",
        lastNameLabel: "Apellido",
        lastNamePlaceholder: "Ej: Martínez",
        gender: {
          label: "Género",
          options: {
            select: "Seleccione...",
            f: "Femenino",
            m: "Masculino",
            x: "Otro",
          },
        },
        birthDateLabel: "Fecha de nacimiento",
        phoneLabel: "Teléfono",
        phonePlaceholder: "Ej: +1 305 555 1234",
        emailLabel: "Correo electrónico",
        emailPlaceholder: "Ej: nombre@correo.com",
        termsLabel:
          'Acepto los <a target="_blank" href="https://thefutureinsurance.com/terminos-y-condiciones/">términos de uso</a> y la <a target="_blank" href="https://thefutureinsurance.com/politica-de-privacidad/">política de privacidad</a>',
        whatsappConsentLabel:
          "Acepto recibir información y seguimiento por WhatsApp sobre mi solicitud.",
        errors: {
          firstName: "Ingresa el nombre del responsable",
          lastName: "Ingresa el apellido del responsable",
          birthDate: "Seleccione la fecha de nacimiento",
          phone: "Ingresa un teléfono válido",
          email: "Ingresa un correo electrónico",
          emailInvalid: "Ingresa un correo electrónico válido",
          terms: "Debe aceptar los términos para continuar",
          whatsappConsent: "Debes aceptar el seguimiento por WhatsApp",
        },
      },
      members: {
        titleCouple: "Datos del segundo integrante",
        titleFamily: "Integrantes del hogar",
        helper: "Complete los datos de cada integrante adicional",
        primaryLabel: "Titular",
        familyCountLabel: "¿Cuántos integrantes adicionales tenés?",
        familyCountHint:
          "Incluí cónyuge y dependientes que compartirán el plan",
        memberTitle: "Integrante #{{number}}",
        roles: {
          primary: "Titular",
          spouse: "Cónyuge",
          dependent: "Dependiente",
        },
        firstNameLabel: "Nombre",
        lastNameLabel: "Apellido",
        genderLabel: "Género",
        birthDateLabel: "Fecha de nacimiento",
        errors: {
          firstName: "Ingresa el nombre",
          lastName: "Ingresa el apellido",
          birthDate: "Seleccione la fecha de nacimiento",
          completeCouple:
            "Para hogares en pareja necesitas ingresar los datos del cónyuge.",
        },
      },
      summary: {
        title: "Revise y confirme sus datos",
        helper: "Verifique la información antes de continuar",
        planTitle: "Plan seleccionado",
        householdTitle: "Resumen del hogar",
        zipCode: "Código postal",
        county: "Condado",
        income: "Ingreso familiar",
        primaryTitle: "Datos de contacto",
        consentTitle: "Firma electrónica",
        consentSubTitle: "Por favor revise y firme el documento",
        consentStatus: {
          not_requested: "Aún no solicitada",
          pending: "Esperando firma",
          signed: "Firmado",
          declined: "Rechazado",
        },
        openSignature: "Abrir documento de firma",
        checkConsent: "Verificar estado",
        checkingConsent: "Verificando...",
        confirmButton: "Confirmar datos",
        emailRequired: "El correo electrónico es obligatorio",
        sending: "Enviando...",
        consentSignedTitle: "Firma completada",
        consentSignedSubtitle: "Recibimos el consentimiento correctamente.",
        closePage: "Puede cerrar esta página cuando termine de revisar.",
      },
      errors: {
        generic: "Ocurrió un error inesperado. Intentá nuevamente.",
        identityRequired:
          "Debes completar la verificación de identidad antes de continuar.",
      },
      loading: "Procesando...",
    },
    identity: {
      otp: {
        title: "Verificación de identidad",
        helper:
          "Para proteger tu información enviamos un código de verificación.",
        contextTitle: "Verificamos tu identidad para mostrarte planes exactos",
        contextDesc:
          "Este paso protege tu información y garantiza que los planes y precios que verás son precisamente los que corresponden a tu perfil. Solo toma 30 segundos.",
        step1: "Elige cómo recibir el código",
        step2: "Ingresa el código de 6 dígitos",
        step3: "Ver tus planes disponibles",
        channelsRequired:
          "Seleccione al menos un canal para recibir el código.",
        codeRequired: "Ingresa el código que recibiste.",
        sendSmsWithPhone: "Enviar SMS a {{phone}}",
        sendSms: "Enviar código por SMS",
        sendEmailWithAddress: "Enviar email a {{email}}",
        sendEmail: "Enviar código por email",
        sending: "Enviando...",
        sendCode: "Enviar código",
        smsSent: "Enviamos un código por SMS a {{phone}}.",
        emailSent: "Enviamos un código por email a {{email}}.",
        codeLabel: "Código de verificación",
        verifying: "Validando...",
        verifyButton: "Validar código",
      },
    },
    quote: {
      otp: {
        title: "Código de seguridad",
        helper:
          "Antes de generar la cotización necesitamos que confirmes el código enviado.",
        contextTitle: "Confirmación de seguridad",
        contextDesc:
          "Este paso protege tu información y garantiza que los planes y precios que verás son precisamente los que corresponden a tu perfil. Solo toma 30 segundos.",
        step1: "Elige cómo recibir el código",
        step2: "Ingresa el código de 6 dígitos",
        step3: "Ver tus planes disponibles",
        channelsRequired:
          "Seleccione al menos un canal para recibir el código.",
        codeRequired: "Ingresa el código que recibiste.",
        sendSmsWithPhone: "Enviar SMS a {{phone}}",
        sendSms: "Enviar código por SMS",
        sendEmailWithAddress: "Enviar email a {{email}}",
        sendEmail: "Enviar código por email",
        sending: "Enviando...",
        sendCode: "Enviar código",
        smsSent: "Enviamos un código por SMS a {{phone}}.",
        emailSent: "Enviamos un código por email a {{email}}.",
        codeLabel: "Código de seguridad",
        verifying: "Verificando...",
        verifyButton: "Verificar código",
      },
      holder: {
        title: "Completemos los datos del titular",
        helper: "Necesitamos la dirección y el SSN para generar la cotización.",
        firstName: "Nombre",
        lastName: "Apellido",
        email: "Correo electrónico",
        phone: "Teléfono",
        address1: "Dirección (línea 1)",
        address2: "Dirección (línea 2)",
        city: "Ciudad",
        stateCode: "Estado (código)",
        stateName: "Estado (nombre)",
        zipCode: "Código postal",
        countyFips: "Código del condado",
        location: "Ubicación",
        county: "Condado",
        changeZip: "Cambiar código postal",
        zipSearchPlaceholder: "Escribe el código postal",
        ssn: "Número de Seguro Social",
        usesTobacco: "¿Usa tabaco?",
        isPregnant: "¿Está embarazada?",
        saving: "Guardando...",
        errors: {
          email: "Ingresa un correo electrónico válido.",
          address1: "Ingresa la dirección principal.",
          city: "Ingresa la ciudad.",
          stateCode: "Ingresa el estado (código).",
          zipCode: "Ingresa el código postal.",
          zipNotFound: "No encontramos ese código postal.",
          ssn: "Ingresa el número de Seguro Social.",
        },
      },
      members: {
        title: "Datos de los integrantes",
        helper:
          "Complete la relación y datos de contacto de cada integrante adicional.",
        relationship: "Relación",
        relationshipPlaceholder: "Seleccione una relación",
        email: "Correo electrónico",
        phone: "Teléfono",
        usesTobacco: "¿Usa tabaco?",
        isPregnant: "¿Está embarazada?",
        saving: "Guardando...",
        errors: {
          relationship: "Seleccione la relación de cada integrante.",
        },
      },
      payment: {
        title: "Información de pago",
        helper: "Indicanos cómo se cobrará la póliza cuando sea aprobada.",
        methodLabel: "Método de pago",
        methodCard: "Tarjeta de crédito o débito",
        methodBank: "Cuenta bancaria",
        cardNumber: "Número de tarjeta",
        cardType: "Tipo de tarjeta",
        cardCompany: "Compañía",
        expirationMonth: "Mes de expiración",
        expirationYear: "Año de expiración",
        cvv: "CVV",
        cardTypeCredit: "Tarjeta de crédito",
        cardTypeDebit: "Tarjeta de débito",
        bankName: "Nombre del banco",
        accountNumber: "Número de cuenta",
        abaRouting: "Routing/ABA",
        paymentHolder: "Titular del método de pago",
        billingAddress1: "Dirección de facturación (línea 1)",
        billingAddress2: "Dirección de facturación (línea 2)",
        billingCity: "Ciudad de facturación",
        billingStateCode: "Estado de facturación",
        billingZipCode: "Código postal de facturación",
        confirmAccuracy: "Confirmo que los datos proporcionados son correctos.",
        provideLater: "Proveeré los datos de pago más tarde.",
        submitting: "Creando cotización...",
        submit: "Crear cotización",
        errors: {
          confirmAccuracy: "Debe confirmar que los datos son correctos.",
          cardNumber: "Ingresa el número de la tarjeta.",
          cardNumberInvalid: "Ingresa un número de tarjeta válido.",
          expiration: "Ingresa el mes y año de expiración.",
          expirationInvalid: "Ingresa un mes y año de expiración válidos.",
          expirationPast: "La tarjeta ya se encuentra vencida.",
          cvv: "Ingresa el código de seguridad.",
          cvvInvalid: "Ingresa un código de seguridad válido.",
          bankName: "Ingresa el nombre del banco.",
          accountNumber: "Ingresa el número de cuenta.",
          abaRouting: "Ingresa el número de routing/ABA.",
          billingAddress:
            "Complete la dirección de facturación (calle, ciudad, estado y código postal).",
        },
      },
      success: {
        title: "¡Listo! Generamos tu cotización",
        messageTitle:
          "¡Felicitaciones! En los próximos minutos estarás asegurado.",
        messageBody:
          "Estamos procesando tu información y activando tu cobertura.",
        messageFollowUp:
          "Sus tarjetas de seguro están siendo procesadas actualmente y se enviarán pronto.<br><br> 📩 Revise su correo electrónico pronto — le enviaremos todos los detalles sobre su plan.",
        planSummaryTitle: "Resumen del plan seleccionado",
        planName: "Plan",
        planCarrier: "Aseguradora",
        planPremium: "Pago mensual",
        policyId: "ID de cotización: {{policyId}}",
        finish: "Finalizar",
      },
    },
    householdForm: {
      basicInfoTitle: "Información básica del hogar",
      zipCodeLabel: "Código postal",
      zipCodePlaceholder: "Ej: 12345",
      incomeLabel: "Ingreso familiar anual",
      incomePlaceholder: "Ej: 25000",
      memberQuantityLabel: "Cantidad de miembros",
      membersTitleSingular: "Integrante familiar",
      membersTitlePlural: "Integrantes familiares",
      membersHelper:
        "Usamos esta información para recomendar planes que se ajusten a las edades y necesidades del hogar.",
      ageLabel: "Edad",
      agePlaceholder: "Ej: 34",
      genderLabel: "Género",
      memberHeading: "Integrante #{{number}}",
      dateOfBirthLabel: "Fecha de nacimiento",
      dateOfBirthPlaceholder: "Seleccione una fecha",
      age: "Edad: {{age}} años",
      agePending: "Ingresa fecha de nacimiento",
      errors: {
        zipCode: "Ingresa un código postal válido.",
        income: "Ingresa un ingreso familiar mensual mayor a cero.",
        memberAge: "Verifice las edades de cada integrante del hogar.",
        memberDob:
          "Ingresa la fecha de nacimiento de cada integrante del hogar.",
      },
    },
    genders: {
      female: "Femenino",
      male: "Masculino",
    },
    planSelection: {
      helper:
        "Estos planes provienen del marketplace de seguros y se adaptan a los datos que cargaste. Seleccione uno para revisar sus detalles.",
      loading: "Cargando planes...",
      error: "No pudimos recuperar los planes. Intentá nuevamente.",
      householdChip: "CP {{zipCode}} • {{count}} {{label}}",
      memberWordSingular: "miembro",
      memberWordPlural: "miembros",
      monthlyPremium: "Prima mensual",
      deductible: "Deducible",
      outOfPocketMax: "Tope de bolsillo",
      recommended: "Recomendado",
      carriersLabel: "Compara planes de los principales proveedores",
      yearToggle: {
        title: "Año de cobertura",
        option: "Planes {{year}}",
      },
      missingDemographics: {
        title: "Completa tus datos para cotizar",
        helper: "Para poder consultar los planes del marketplace, necesitamos tu género y fecha de nacimiento.",
        genderLabel: "Género del titular",
        genderPlaceholder: "Selecciona...",
        birthDateLabel: "Fecha de nacimiento del titular",
        ageLabel: "Edad: {{age}} años",
        emailLabel: "Correo electrónico del titular",
        emailPlaceholder: "Ej: nombre@correo.com",
        emailInvalid: "Ingresa un correo electrónico válido.",
        submit: "Ver planes",
        error: "Por favor, completa los campos requeridos.",
      },
    },
    planModal: {
      metaLabelPremium: "Prima mensual",
      metaLabelDeductible: "Deducible",
      metaLabelOutOfPocket: "Tope de bolsillo",
    },
    detailsPage: {
      selectedPlanTitle: "Plan seleccionado",
      householdSummaryTitle: "Resumen del hogar",
      applicantTitle: "Datos del responsable",
      incomeLabel: "Ingreso familiar",
      membersLabel: "Miembros",
      firstNameLabel: "Nombre",
      firstNamePlaceholder: "Ej: Ana",
      lastNameLabel: "Apellido",
      lastNamePlaceholder: "Ej: Martínez",
      dateOfBirthLabel: "Fecha de nacimiento",
      phoneLabel: "Celular",
      phonePlaceholder: "Ej: +54 9 11 1234-5678",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "Ej: nombre@correo.com",
      messageLabel: "Mensaje adicional",
      messagePlaceholder:
        "Cuéntanos si necesitas información extra para que te contactemos.",
      memberSummary: "Integrante #{{number}}: {{age}} años • {{gender}}",
      errors: {
        name: "Complete el nombre y apellido del responsable del hogar.",
        birthdate: "Ingresa la fecha de nacimiento.",
        phone: "Ingresa un número de celular.",
        email: "Ingresa un correo electrónico válido.",
      },
    },
    confirmation: {
      thankYou: "¡Gracias, {{name}}!",
      genericName: "familia",
      message:
        "Recibimos los datos del hogar y el plan {{plan}}. Un asesor de TFI se comunicará a la brevedad para continuar con la gestión.",
      summaryTitle: "Resumen enviado",
      responsibleLabel: "Responsable",
      planLabel: "Plan",
      incomeLabel: "Ingreso familiar",
      membersLabel: "Miembros",
      contactTitle: "Datos de contacto",
      phoneLabel: "Celular",
      phonePlaceholder: "Ej: +54 9 11 1234-5678",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "Ej: nombre@correo.com",
      messageLabel: "Mensaje adicional",
      messagePlaceholder:
        "Cuéntanos si necesitas información extra o preferís un horario de contacto.",
      submitContact: "Enviar información",
      linkTitle: "Link de firma",
    },
  },
  en: {
    header: {
      title: "TFI Marketplace",
      subtitle: "We guide your household to find the best health coverage.",
      languageLabel: "Language",
      callus: "Speak with a specialist",
      // Same source as the static HTML: REACT_APP_GOOGLE_ADS_PHONE_EN (.env)
      phone: process.env.REACT_APP_GOOGLE_ADS_PHONE_EN ?? "",
      languageToggleLabel: "Switch language to {{language}}",
      options: {
        es: "Spanish",
        en: "English",
      },
      carrierCompareLabel: "Compare plans from top insurance providers",
    },
    steps: {
      household: "Household information",
      plans: "Choose a plan for your family",
      details: "Complete the primary contact details",
      confirmation: "Confirmation",
    },
    footer: {
      line1:
        "Requests, calls, and invitations for insurance on this website are made through Max Capital 97 LLC, only where licensed and appointed.",
      line2:
        "Max Capital 97 LLC National Producer Number is 19966989 and its state license numbers are listed below.",
      line3:
        "The Future Insurance is a private insurance agency and is not affiliated with, endorsed by, or operated by the U.S. government, HealthCare.gov, or any state or federal Health Insurance Marketplace. We are not the Marketplace website. We are a licensed agency that helps consumers find, compare, and enroll in ACA health plans through Marketplace-approved partners. By submitting your information, you agree to be contacted by a licensed agent.",
      friendlyText:
        "Helping you find the best health coverage with transparency and the care you deserve.",
      line4: "Copyright ©{{year}} | The Future Insurance. All rights reserved.",
    },
    benefits: {
      preTitle: "OUR AGENTS OFFER PRIVATE AND ACA-COMPLIANT PLANS",
      title1: "Health Insurance",
      title2: "Benefits",
      footerText: "Among other essential health benefits covered by law.",
      list: [
        { text: "Emergency services" },
        { text: "Ambulatory patient services" },
        { text: "Hospitalization" },
        { text: "Care before and after your baby is born" },
        { text: "Mental health and substance abuse services" },
        { text: "Your prescription drugs" },
        { text: "Physical therapy, occupational therapy, and rehabilitation" },
        { text: "Laboratory services" },
        { text: "Preventive services: screenings, vaccines, and counseling" },
        { text: "Pediatric services: dental and vision care" }
      ]
    },
    faq: {
      preTitle: "Frequently Asked Questions",
      title: "Got <blue>Questions?</blue>",
      subtitle: "Answers to the most common questions about health insurance",
      questions: [
        {
          q: "How much does it cost to enroll?",
          a: "Enrolling with us is completely free. We do not charge for advice or helping you review your options. You only pay the monthly premium of the plan you choose. Also, many people qualify for tax credits that can reduce the monthly cost. Eligibility varies."
        },
        {
          q: "Do I qualify for subsidies or financial help?",
          a: "In many cases, yes. Eligibility depends on factors like your income, family size, and where you live. During the call, our agents review your case and let you know if you qualify for help to lower the cost of your plan."
        },
        {
          q: "What documents do I need to apply?",
          a: "Normally they will ask for your Social Security number or applicable immigration document, income information like W-2, pay stubs or tax returns, and the details of the family members you want to include in the coverage."
        },
        {
          q: "Do I need to be a US citizen to enroll?",
          a: "Not necessarily. Some people with lawful permanent residence or other eligible immigration statuses may also qualify for Marketplace plans. Eligibility depends on your specific immigration situation."
        },
        {
          q: "How long does the enrollment process take?",
          a: "In most cases, the process can be completed in a single call, provided you have your basic information and documents available."
        },
        {
          q: "Can I enroll if I currently don't have health insurance?",
          a: "Yes. If you don't currently have coverage, we can review what options are available to you and whether you qualify for a special enrollment period or open enrollment, depending on your case."
        },
        {
          q: "Can I include my spouse, children, or family in the same plan?",
          a: "Yes. Depending on the plan and your situation, you can include family members in the same application and review coverage options for everyone."
        },
        {
          q: "What if I already have a plan and want to change it?",
          a: "We can review your current plan and help you compare other available options. In some cases, you may be able to change plans if you qualify for a special enrollment period or during open enrollment."
        },
        {
          q: "How do I know which plan is right for me?",
          a: "Our agents help you compare plans based on your budget, medications, preferred doctors, and medical needs. The goal is to help you find an option that fits your situation."
        }
      ]
    },
    buttons: {
      back: "Back",
      cancel: "Cancel",
      continue: "Continue",
      viewDetails: "View details",
      close: "Close",
      selectPlan: "Choose plan",
      submitApplicant: "Submit information",
      startOver: "Start over",
    },
    wizard: {
      stepCounter: "Phase {{current}} of {{total}} — {{phase}}",
      titles: {
        householdType: "Quote Type",
        householdBasics: "Household basics",
        primary: "Primary contact",
        members: "Additional members",
        identityOtp: "Identity verification",
        plans: "Select a plan",
        summary: "Review and confirm",
        consent: "Electronic signature",
        extra: "Additional information",
        quoteHolder: "Policy holder details",
        quoteMembers: "Plan members",
        quotePayment: "Payment information",
        quoteSuccess: "Policy being prepared",
      },
      coverageType: {
        title: "What kind of coverage are you looking for?",
        helper: "Select an option to get started",
        options: {
          comprehensive: {
            title: "Most comprehensive coverage",
            desc: "Major medical plans with full protection",
          },
          specific: {
            title: "A plan with specific benefits",
            desc: "Coverage focused on your needs",
          },
          decide: {
            title: "Help me decide the right plan",
            desc: "Our experts guide you",
          },
          know: {
            title: "I already know which plan I want",
            desc: "Talk to an agent to enroll",
          },
          medicare: {
            title: "Medicare options",
            desc: "For those 65 and older",
          },
        },
      },
      common: {
        yearsOld: "years",
        dateOfBirth: {
          day: "Day",
          month: "Month",
          year: "Year",
          months: {
            "01": "January", "02": "February", "03": "March", "04": "April",
            "05": "May", "06": "June", "07": "July", "08": "August",
            "09": "September", "10": "October", "11": "November", "12": "December",
          },
        },
      },
      householdType: {
        title: "How would you like your quote?",
        helper:
          "Choose the option that best represents you to tailor the experience",
        options: {
          soltero: "Single",
          pareja: "Couple",
          familia: "Family",
        },
      },
      householdBasics: {
        title: "Enter your zip code and income",
        helper:
          "We use this information to find plans available for {{household}}",
        zipCodeLabel: "Zip code",
        loadingZip: "Looking up zip code...",
        countyLabel: "County",
        incomeLabel: "Annual household income",
        incomePlaceholder: "E.g. 45000",
        errors: {
          zip: "Enter a valid zip code",
          income: "Enter an income greater than zero",
        },
      },
      primary: {
        title: "Tell us about the primary contact",
        helper: "We use this information to keep you informed of your request",
        firstNameLabel: "First name",
        firstNamePlaceholder: "E.g. John",
        lastNameLabel: "Last name",
        lastNamePlaceholder: "E.g. Smith",
        gender: {
          label: "Gender",
          options: {
            select: "Select...",
            f: "Female",
            m: "Male",
            x: "Other",
          },
        },
        birthDateLabel: "Date of birth",
        phoneLabel: "Phone",
        phonePlaceholder: "E.g. +1 305 555 1234",
        emailLabel: "Email",
        emailPlaceholder: "E.g. name@email.com",
        termsLabel:
          'I accept the <a target="_blank" href="https://thefutureinsurance.com/en/terms-conditions/">terms of use</a> and <a target="_blank" href="https://thefutureinsurance.com/en/privacy-policy/">privacy policy</a>',
        whatsappConsentLabel:
          "I agree to receive information and follow-up via WhatsApp regarding my request.",
        errors: {
          firstName: "Enter the primary contact first name",
          lastName: "Enter the primary contact last name",
          birthDate: "Select the date of birth",
          phone: "Enter a valid phone number",
          email: "Enter an email address",
          emailInvalid: "Enter a valid email address",
          terms: "You must accept the terms to continue",
          whatsappConsent: "You must accept WhatsApp follow-up",
        },
      },
      members: {
        titleCouple: "Second member details",
        titleFamily: "Household members",
        helper: "Complete the information for each additional member",
        primaryLabel: "Primary",
        familyCountLabel: "How many additional members?",
        familyCountHint:
          "Include spouse and dependents that will share the plan",
        memberTitle: "Member #{{number}}",
        roles: {
          primary: "Primary",
          spouse: "Spouse",
          dependent: "Dependent",
        },
        firstNameLabel: "First name",
        lastNameLabel: "Last name",
        genderLabel: "Gender",
        birthDateLabel: "Date of birth",
        errors: {
          firstName: "Enter the first name",
          lastName: "Enter the last name",
          birthDate: "Select the date of birth",
          completeCouple:
            "Couple households must include the spouse information.",
        },
      },
      summary: {
        title: "Review and confirm",
        helper: "Verify the information before continuing",
        planTitle: "Selected plan",
        householdTitle: "Household summary",
        zipCode: "Zip code",
        county: "County",
        income: "Household income",
        primaryTitle: "Contact details",
        consentTitle: "Electronic signature",
        consentSubTitle: "Please review and sign the document",
        consentStatus: {
          not_requested: "Not requested yet",
          pending: "Waiting for signature",
          signed: "Signed",
          declined: "Declined",
        },
        openSignature: "Open signature document",
        checkConsent: "Check status",
        checkingConsent: "Checking...",
        confirmButton: "Confirm information",
        emailRequired: "The email is required",
        sending: "Sending...",
        consentSignedTitle: "Signature completed",
        consentSignedSubtitle: "We have received the consent successfully.",
        closePage: "You can close this page once you finish reviewing.",
      },
      errors: {
        generic: "Something went wrong. Please try again.",
        identityRequired:
          "You must complete identity verification before continuing.",
      },
      loading: "Processing...",
    },
    identity: {
      otp: {
        title: "Verify your identity",
        helper: "We send a verification code to protect your information.",
        contextTitle: "We verify your identity to show you accurate plans",
        contextDesc:
          "This step protects your information and ensures the plans and prices you see are exactly the ones that match your profile. It only takes 30 seconds.",
        step1: "Choose how to receive the code",
        step2: "Enter the 6-digit code",
        step3: "View your available plans",
        channelsRequired: "Select at least one channel to receive the code.",
        codeRequired: "Enter the code you received.",
        sendSmsWithPhone: "Send SMS to {{phone}}",
        sendSms: "Send code via SMS",
        sendEmailWithAddress: "Send email to {{email}}",
        sendEmail: "Send code via email",
        sending: "Sending...",
        sendCode: "Send code",
        smsSent: "We sent a code by SMS to {{phone}}.",
        emailSent: "We sent a code by email to {{email}}.",
        codeLabel: "Verification code",
        verifying: "Verifying...",
        verifyButton: "Verify code",
      },
    },
    quote: {
      otp: {
        title: "Security code",
        helper:
          "Before generating the quote we need you to confirm the security code we sent.",
        contextTitle: "Security confirmation",
        contextDesc:
          "This step protects your information and ensures the plans and prices you see are exactly the ones that match your profile. It only takes 30 seconds.",
        step1: "Choose how to receive the code",
        step2: "Enter the 6-digit code",
        step3: "View your available plans",
        channelsRequired: "Select at least one channel to receive the code.",
        codeRequired: "Enter the code you received.",
        sendSmsWithPhone: "Send SMS to {{phone}}",
        sendSms: "Send code via SMS",
        sendEmailWithAddress: "Send email to {{email}}",
        sendEmail: "Send code via email",
        sending: "Sending...",
        sendCode: "Send code",
        smsSent: "We sent a code by SMS to {{phone}}.",
        emailSent: "We sent a code by email to {{email}}.",
        codeLabel: "Security code",
        verifying: "Verifying...",
        verifyButton: "Verify code",
      },
      holder: {
        title: "Complete the policy holder details",
        helper: "We need the address and SSN to generate the quote.",
        firstName: "First name",
        lastName: "Last name",
        email: "Email",
        phone: "Phone",
        address1: "Address (line 1)",
        address2: "Address (line 2)",
        city: "City",
        stateCode: "State (code)",
        stateName: "State (name)",
        zipCode: "Zip code",
        countyFips: "County code",
        location: "Location",
        county: "County",
        changeZip: "Change ZIP code",
        zipSearchPlaceholder: "Enter ZIP code",
        ssn: "Social Security Number",
        usesTobacco: "Uses tobacco",
        isPregnant: "Is pregnant",
        saving: "Saving...",
        errors: {
          email: "Enter a valid email address.",
          address1: "Enter the primary address.",
          city: "Enter the city.",
          stateCode: "Enter the state code.",
          zipCode: "Enter the zip code.",
          zipNotFound: "We could not find that ZIP code.",
          ssn: "Enter the Social Security Number.",
        },
      },
      members: {
        title: "Additional member details",
        helper: "Provide the relationship and contact details for each member.",
        relationship: "Relationship",
        relationshipPlaceholder: "Select a relationship",
        email: "Email",
        phone: "Phone",
        usesTobacco: "Uses tobacco",
        isPregnant: "Is pregnant",
        saving: "Saving...",
        errors: {
          relationship: "Select the relationship for each member.",
        },
      },
      payment: {
        title: "Payment information",
        helper: "Tell us how the policy will be charged once approved.",
        methodLabel: "Payment method",
        methodCard: "Credit or debit card",
        methodBank: "Bank account",
        cardNumber: "Card number",
        cardType: "Card type",
        cardCompany: "Company",
        expirationMonth: "Expiration month",
        expirationYear: "Expiration year",
        cvv: "CVV",
        cardTypeCredit: "Credit card",
        cardTypeDebit: "Debit card",
        bankName: "Bank name",
        accountNumber: "Account number",
        abaRouting: "Routing/ABA",
        paymentHolder: "Payment method holder",
        billingAddress1: "Billing address (line 1)",
        billingAddress2: "Billing address (line 2)",
        billingCity: "Billing city",
        billingStateCode: "Billing state",
        billingZipCode: "Billing zip code",
        confirmAccuracy: "I confirm the information provided is correct.",
        provideLater: "I'll provide the payment details later.",
        submitting: "Creating quote...",
        submit: "Create quote",
        errors: {
          confirmAccuracy: "You must confirm the information is correct.",
          cardNumber: "Enter the card number.",
          cardNumberInvalid: "Enter a valid card number.",
          expiration: "Enter the expiration month and year.",
          expirationInvalid: "Enter a valid expiration month and year.",
          expirationPast: "The card expiration date is in the past.",
          cvv: "Enter the security code.",
          cvvInvalid: "Enter a valid security code.",
          bankName: "Enter the bank name.",
          accountNumber: "Enter the account number.",
          abaRouting: "Enter the routing/ABA number.",
          billingAddress:
            "Provide the billing address (street, city, state, and zip code).",
        },
      },
      success: {
        title: "All set! Your policy is being generated!",
        messageTitle: "Congratulations! Your coverage is almost ready.",
        messageBody:
          "We are processing your information and activating your plan.",
        messageFollowUp:
          "Your insurance cards are currently being processed and will be delivered soon. <br> <br> 📩 Check your email soon — we'll send you all the details about your plan.",
        planSummaryTitle: "Selected plan summary",
        planName: "Plan",
        planCarrier: "Carrier",
        planPremium: "Monthly premium",
        policyId: "Quote ID: {{policyId}}",
        finish: "Finish",
      },
    },
    householdForm: {
      basicInfoTitle: "Basic household information",
      zipCodeLabel: "Zip code",
      zipCodePlaceholder: "E.g. 12345",
      incomeLabel: "Household annual income",
      incomePlaceholder: "E.g. 15000",
      memberQuantityLabel: "Number of members",
      membersTitleSingular: "Household member",
      membersTitlePlural: "Household members",
      membersHelper:
        "We use this information to recommend plans that match your household's ages and needs.",
      ageLabel: "Age",
      agePlaceholder: "E.g. 34",
      genderLabel: "Gender",
      memberHeading: "Member #{{number}}",
      dateOfBirthLabel: "Date of birth",
      dateOfBirthPlaceholder: "Select a date",
      age: "Age: {{age}} years",
      agePending: "Enter the date of birth.",
      errors: {
        zipCode: "Enter a valid zip code.",
        income: "Enter a monthly household income greater than zero.",
        memberAge: "Review each household member's age.",
        memberDob: "Enter the date of birth.",
      },
    },
    genders: {
      female: "Female",
      male: "Male",
    },
    planSelection: {
      helper:
        "These plans come from the insurance marketplace and adapt to the information you provided. Select one to review its details.",
      loading: "Loading plans...",
      error: "We couldn't retrieve the plans. Please try again.",
      householdChip: "ZIP {{zipCode}} • {{count}} {{label}}",
      memberWordSingular: "member",
      memberWordPlural: "members",
      monthlyPremium: "Monthly premium",
      deductible: "Deductible",
      outOfPocketMax: "Out-of-pocket max",
      recommended: "Recommended",
      carriersLabel: "Compare plans from top insurance providers",
      yearToggle: {
        title: "Coverage year",
        option: "Plans {{year}}",
      },
      missingDemographics: {
        title: "Complete your details to get quotes",
        helper: "To check the marketplace plans, we need your gender and date of birth.",
        genderLabel: "Policyholder gender",
        genderPlaceholder: "Select...",
        birthDateLabel: "Policyholder date of birth",
        ageLabel: "Age: {{age}} years",
        emailLabel: "Policyholder email",
        emailPlaceholder: "E.g. name@email.com",
        emailInvalid: "Enter a valid email address.",
        submit: "View plans",
        error: "Please complete the required fields.",
      },
    },
    planModal: {
      metaLabelPremium: "Monthly premium",
      metaLabelDeductible: "Deductible",
      metaLabelOutOfPocket: "Out-of-pocket max",
    },
    detailsPage: {
      selectedPlanTitle: "Selected plan",
      householdSummaryTitle: "Household summary",
      applicantTitle: "Primary contact details",
      incomeLabel: "Household income",
      membersLabel: "Members",
      firstNameLabel: "First name",
      firstNamePlaceholder: "E.g. Ana",
      lastNameLabel: "Last name",
      lastNamePlaceholder: "E.g. Martínez",
      dateOfBirthLabel: "Date of birth",
      phoneLabel: "Cell phone",
      phonePlaceholder: "E.g. +1 555 123 4567",
      emailLabel: "Email",
      emailPlaceholder: "E.g. name@email.com",
      messageLabel: "Additional message",
      messagePlaceholder:
        "Let us know if there's anything else we should consider when contacting you.",
      memberSummary: "Member #{{number}}: {{age}} years • {{gender}}",
      errors: {
        name: "Please fill in the household primary contact's first and last name.",
        birthdate: "Enter a date of birth.",
        phone: "Enter a cell phone number.",
        email: "Enter a valid email address.",
      },
    },
    confirmation: {
      thankYou: "Thank you, {{name}}!",
      genericName: "family",
      message:
        "We received the household details and the {{plan}} plan. A TFI advisor will contact you shortly to continue the process.",
      summaryTitle: "Summary sent",
      responsibleLabel: "Primary contact",
      planLabel: "Plan",
      incomeLabel: "Household income",
      membersLabel: "Members",
      contactTitle: "Contact information",
      phoneLabel: "Cell phone",
      phonePlaceholder: "E.g. +1 555 123 4567",
      emailLabel: "Email",
      emailPlaceholder: "E.g. name@email.com",
      messageLabel: "Additional message",
      messagePlaceholder:
        "Let us know if you need extra information or prefer a specific time to be contacted.",
      submitContact: "Send information",
      linkTitle: "Signing link",
    },
  },
};
